/**
 * Reducer for resource-level actions (state.resourceActions).
 * Consumes actions dispatched by ResourceActionsSaga and stores the result from /api/resourceActions.
 * Selectors (canPerformAction, getActionsForResourceType) read from this state.
 */

import { Reducer } from 'redux';

import {
  FETCH_RESOURCE_ACTIONS_FAILURE,
  FETCH_RESOURCE_ACTIONS_REQUEST,
  FETCH_RESOURCE_ACTIONS_SUCCESS,
  ResourceActionTypes
} from '../actions/ResourceActions';
import { IResourceActionsState } from '../types/ResourceActionTypes';

/** Empty state before any fetch; also the default when fetch has not been triggered. */
const initialState: IResourceActionsState = {
  actionsByResourceType: new Map(),
  isFetching: false,
  fetchError: undefined
};

export const resourceActionsReducer: Reducer<IResourceActionsState> = (
  state = initialState,
  action
): IResourceActionsState => {
  const typedAction = action as ResourceActionTypes;

  switch (typedAction.type) {
    /** Saga has started the API call; used by router to allow routing while loading. */
    case FETCH_RESOURCE_ACTIONS_REQUEST:
      return {
        ...state,
        isFetching: true,
        fetchError: undefined
      };

    /** API succeeded; store payload as Map<resourceType, Set<actionNames>> for O(1) lookups. */
    case FETCH_RESOURCE_ACTIONS_SUCCESS: {
      const actionsByResourceType = new Map<string, Set<string>>();
      Object.entries(typedAction.payload).forEach(([resourceType, actions]) => {
        actionsByResourceType.set(resourceType, new Set(actions));
      });

      return {
        ...state,
        actionsByResourceType,
        isFetching: false,
        fetchError: undefined
      };
    }

    /** API failed; keep previous actionsByResourceType, surface error for diagnostics. */
    case FETCH_RESOURCE_ACTIONS_FAILURE:
      return {
        ...state,
        isFetching: false,
        fetchError: typedAction.error
      };

    default:
      return state;
  }
};
